import React, {
  useEffect,
  useState,
} from 'react';

const STEPS = [
  {
    type: 'lesson',
    narration:
      'Now that you understand how Tribe works, let’s talk about why you’re here. The Tribe Brand Ambassador Program exists to help grow our membership. Your role is to find people who are a strong fit for Tribe, reach out directly using the scripts we provide, and get them to create an account and attend their first gathering. When someone creates an account using your referral code and attends their first gathering, we call that a qualified referral—and you get paid for every one.',
  },
  {
  type: 'question',
  prompt:
    'What is the primary responsibility of a Tribe Brand Ambassador?',
  choices: [
    {
      value: 'A',
      label:
        'Find strong prospects, contact them personally, and help them create an account and attend their first gathering',
    },
    {
      value: 'B',
      label:
        'Plan and host Tribe gatherings for new members',
    },
    {
      value: 'C',
      label:
        'Promote Tribe through public social-media posts',
    },
  ],
  correct: 'A',
  explanation:
    'Ambassadors identify strong prospects and help them complete the journey toward becoming qualified referrals.',
},
];

const STAGE_TIMES = [
  300,
  4300,

  8200,
  11200,
  14200,

  // create account -> first gathering
  // intentionally quicker
  15600,

  19000,
  21500,
  24000,
];

const RESPONSIBILITIES = [
  {
    action: 'Find',
    remainder: 'strong prospects',
  },
  {
    action: 'Reach out',
    remainder:
      'with the outreach script',
  },
  {
    action: 'Help them',
    remainder:
      'create an account',
  },
{
  action: 'And',
  remainder:
    'attend their first gathering',
},
];

export default function YourRole({
  part,
  Question,
  selected,
  onSelect,
  audioStatus,
  playbackId,
}) {
  const [visualStage, setVisualStage] =
    useState(-1);

  const step = STEPS[part];

  useEffect(() => {
    if (
      part !== 0 ||
      audioStatus !== 'Playing'
    ) {
      return undefined;
    }

    setVisualStage(-1);

    const timers =
      STAGE_TIMES.map(
        (time, index) =>
          setTimeout(() => {
            setVisualStage(index);
          }, time)
      );

    return () => {
      timers.forEach(
        clearTimeout
      );
    };
  }, [
    part,
    audioStatus,
    playbackId,
  ]);

  if (
    step.type === 'question'
  ) {
    return (
      <Question
        {...step}
        selected={selected}
        onSelect={onSelect}
      />
    );
  }

  return (
    <section className="role-kinetic-slide">
      <style>{STYLES}</style>

      {visualStage === 0 && (
        <TypedPhrase
          key="why"
          text="Why you’re here"
        />
      )}

      {visualStage === 1 && (
        <TypedPhrase
          key="grow"
          text="Grow Tribe’s membership"
          highlighted
        />
      )}

      {visualStage >= 2 &&
        visualStage <= 5 && (
          <ResponsibilityList
            visibleCount={
              visualStage - 1
            }
          />
        )}

      {visualStage >= 6 && (
        <QualifiedReferralEquation
          stage={
            visualStage - 5
          }
        />
      )}
    </section>
  );
}

function TypedPhrase({
  text,
  highlighted = false,
}) {
  const words =
    text.split(' ');

  const wordCount =
    words.length;

  /*
   * One single highlight group.
   * "Grow Tribe’s membership"
   * gets one continuous marker stroke.
   */
  const highlightGroups =
    highlighted
      ? [
          {
            words,
            startIndex: 0,
          },
        ]
      : [];

  const [
    visibleWordCount,
    setVisibleWordCount,
  ] = useState(0);

  useEffect(() => {
    setVisibleWordCount(0);

    const timers = [];

    for (
      let index = 0;
      index < wordCount;
      index += 1
    ) {
      timers.push(
        setTimeout(() => {
          setVisibleWordCount(
            index + 1
          );
        }, 120 + index * 220)
      );
    }

    return () => {
      timers.forEach(
        clearTimeout
      );
    };
  }, [
    text,
    wordCount,
  ]);

  const isComplete =
    visibleWordCount ===
    wordCount;

  return (
    <div className="role-single-message">
      <h1 aria-label={text}>
        <span
          className={[
            highlighted
              ? 'marker-highlight'
              : '',
            highlighted &&
            isComplete
              ? 'is-highlighted'
              : '',
          ].join(' ')}
        >
          {highlighted
            ? highlightGroups.map(
                (
                  group,
                  groupIndex
                ) => (
                  <React.Fragment
                    key={
                      group.words.join(
                        '-'
                      )
                    }
                  >
                    <span
                      className="marker-group"
                      style={{
                        '--marker-group-delay': `${
                          180 +
                          group.startIndex *
                            90
                        }ms`,
                      }}
                    >
                      {group.words.map(
                        (
                          word,
                          localIndex
                        ) => {
                          const index =
                            group.startIndex +
                            localIndex;

                          return (
                            <React.Fragment
                              key={`${word}-${index}`}
                            >
                              <span
                                className={[
                                  'role-typed-word',
                                  'marker-word',
                                  index <
                                  visibleWordCount
                                    ? 'is-visible'
                                    : '',
                                ].join(
                                  ' '
                                )}
                                aria-hidden="true"
                              >
                                {
                                  word
                                }
                              </span>

                              {localIndex <
                              group
                                .words
                                .length -
                                1
                                ? ' '
                                : ''}
                            </React.Fragment>
                          );
                        }
                      )}
                    </span>

                    {groupIndex <
                    highlightGroups.length -
                      1
                      ? ' '
                      : ''}
                  </React.Fragment>
                )
              )
            : words.map(
                (
                  word,
                  index
                ) => (
                  <React.Fragment
                    key={`${word}-${index}`}
                  >
                    <span
                      className={[
                        'role-typed-word',
                        index <
                        visibleWordCount
                          ? 'is-visible'
                          : '',
                      ].join(' ')}
                      aria-hidden="true"
                    >
                      {word}
                    </span>

                    {index <
                    words.length - 1
                      ? ' '
                      : ''}
                  </React.Fragment>
                )
              )}
        </span>
      </h1>
    </div>
  );
}

function ResponsibilityList({
  visibleCount,
}) {
  return (
    <div className="role-responsibility-scene">
      <div className="role-responsibility-list">
        {RESPONSIBILITIES.map(
          (
            responsibility,
            index
          ) => {
            const isVisible =
              index <
              visibleCount;

            const isCurrent =
              index ===
              visibleCount - 1;

            return (
              <div
                className={[
                  'role-responsibility-line',
                  isVisible
                    ? 'is-visible'
                    : '',
                  isCurrent
                    ? 'is-current'
                    : '',
                ].join(' ')}
                key={
                  responsibility.action +
                  responsibility.remainder
                }
              >
                <strong className="role-action-handwritten">
                  {
                    responsibility.action
                  }
                </strong>{' '}

                <span>
                  {
                    responsibility.remainder
                  }
                </span>
              </div>
            );
          }
        )}
      </div>
    </div>
  );
}

function QualifiedReferralEquation({
  stage,
}) {
  return (
    <div className="qualified-equation-scene">
      <div
        className={[
          'qualified-equation',
          stage >= 3
            ? 'is-finalizing'
            : '',
        ].join(' ')}
      >

        {/* STEP 01 */}
        <div
          className={[
            'qualified-step',
            'qualified-step-account',
            stage >= 1
              ? 'is-visible'
              : '',
            stage >= 2
              ? 'is-muted'
              : '',
          ].join(' ')}
        >
          <span className="qualified-step-eyebrow">
            Step 01
          </span>

          <div className="qualified-step-copy">
            <strong>
              Registers
            </strong>

            <em className="qualified-step-emphasis">
              with your referral code
            </em>
          </div>
        </div>

        {/* PLUS */}
        <div
          className={[
            'qualified-operator',
            'qualified-plus',
            stage >= 2
              ? 'is-visible'
              : '',
            stage >= 3
              ? 'is-muted'
              : '',
          ].join(' ')}
          aria-hidden="true"
        >
          +
        </div>

        {/* STEP 02 */}
        <div
          className={[
            'qualified-step',
            'qualified-step-attendance',
            stage >= 2
              ? 'is-visible'
              : '',
            stage >= 3
              ? 'is-muted'
              : '',
          ].join(' ')}
        >
          <span className="qualified-step-eyebrow">
            Step 02
          </span>

          <div className="qualified-step-copy">
            <strong>
              Attends their
            </strong>

            <em className="qualified-step-emphasis">
              first gathering
            </em>
          </div>
        </div>

        {/* EQUALS */}
        <div
          className={[
            'qualified-operator',
            'qualified-equals',
            stage >= 3
              ? 'is-visible'
              : '',
          ].join(' ')}
          aria-hidden="true"
        >
          =
        </div>

        {/* STEP 03 */}
        <div
          className={[
            'qualified-step',
            'qualified-step-result',
            stage >= 3
              ? 'is-visible'
              : '',
          ].join(' ')}
        >
          <div className="qualified-result-lockup">
            <strong className="qualified-result-label">
              Qualified
              <br />
              referral
            </strong>

            <svg
              className="qualified-result-circle"
              viewBox="0 0 320 144"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <defs>
                <filter
                  id="qualified-circle-pencil"
                  x="-4%"
                  y="-7%"
                  width="108%"
                  height="114%"
                >
                  <feTurbulence
                    type="fractalNoise"
                    baseFrequency="0.025 0.045"
                    numOctaves="1"
                    seed="8"
                    result="circlePencilNoise"
                  />

                  <feDisplacementMap
                    in="SourceGraphic"
                    in2="circlePencilNoise"
                    scale="0.65"
                    xChannelSelector="R"
                    yChannelSelector="G"
                  />
                </filter>
              </defs>

              <path
                className="qualified-circle-stroke qualified-circle-main"
                pathLength="1"
                d="M17 72 C18 30 79 8 160 8 C241 8 300 30 303 70 C306 111 254 134 161 136 C70 138 17 116 16 79 C16 61 27 45 45 34"
              />

              <path
                className="qualified-circle-stroke qualified-circle-echo"
                pathLength="1"
                d="M7 59 C59 18 148 1 230 11 C267 15 294 25 308 41"
              />
            </svg>

            <div className="qualified-reward-callout">
              <svg
                className="qualified-reward-arrow"
                viewBox="0 0 100 85"
                preserveAspectRatio="xMidYMid meet"
                aria-hidden="true"
              >
                <defs>
                  <filter
                    id="qualified-arrow-pencil"
                    x="-8%"
                    y="-8%"
                    width="116%"
                    height="116%"
                  >
                    <feTurbulence
                      type="fractalNoise"
                      baseFrequency="0.04"
                      numOctaves="1"
                      seed="4"
                      result="arrowPencilNoise"
                    />

                    <feDisplacementMap
                      in="SourceGraphic"
                      in2="arrowPencilNoise"
                      scale="0.55"
                      xChannelSelector="R"
                      yChannelSelector="G"
                    />
                  </filter>
                </defs>

                <path
                  className="qualified-arrow-stroke qualified-arrow-curve"
                  pathLength="1"
                  d="M8 10 C43 13 75 37 86 72"
                />

                <path
                  className="qualified-arrow-stroke qualified-arrow-head"
                  pathLength="1"
                  d="M70 61 L87 74 L91 54"
                />
              </svg>

              <strong>
                $$$$
              </strong>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

const STYLES = `
  .role-kinetic-slide {
    display: flex;
    align-items: center;
    justify-content: center;

    width: 100%;
    min-height: 465px;
    height: 100%;

    padding: 32px;

    box-sizing: border-box;
    overflow: hidden;

    color: #111111;
    background: #ffffff;

    font-family:
      Inter,
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      sans-serif;
  }

  .role-single-message,
  .role-responsibility-scene,
  .qualified-equation-scene {
    display: flex;
    align-items: center;
    justify-content: center;

    width: 100%;
    max-width: 1100px;

    text-align: center;

    animation:
      role-scene-enter
      520ms
      cubic-bezier(
        0.22,
        1,
        0.36,
        1
      )
      both;
  }

  /*
   * MAIN HERO
   */

  .role-single-message h1 {
    width:
      min(
        920px,
        100%
      );

    min-height: 1.3em;

    margin: 0;

    color: #111111;

    font-size:
      clamp(
        42px,
        5.4vw,
        70px
      );

    font-weight: 820;

    line-height: 1;

    letter-spacing:
      -0.045em;

    text-align: center;
  }

  /*
   * YELLOW MARKER
   */

  .marker-highlight {
    display: inline;
  }

  .marker-group {
    position: relative;
    z-index: 1;

    display: inline-block;

    padding:
      0
      0.015em;

    white-space: nowrap;
  }

  .marker-group::before,
  .marker-group::after {
    position: absolute;
    z-index: -1;

    right: -0.045em;
    left: -0.045em;

    content: '';

    transform-origin:
      left center;

    mix-blend-mode:
      multiply;
  }

  .marker-group::before {
    bottom: -0.02em;

    height: 0.68em;

    background:
      linear-gradient(
        178deg,
        rgba(
          230,
          255,
          72,
          0.1
        )
        0%,
        rgba(
          230,
          255,
          72,
          0.7
        )
        14%,
        rgba(
          220,
          249,
          57,
          0.64
        )
        78%,
        rgba(
          230,
          255,
          72,
          0.18
        )
        100%
      );

    clip-path:
      polygon(
        0.5% 19%,
        7% 10%,
        18% 15%,
        31% 7%,
        46% 13%,
        62% 4%,
        78% 10%,
        92% 5%,
        99.5% 17%,
        99% 82%,
        88% 92%,
        71% 87%,
        53% 96%,
        36% 89%,
        20% 98%,
        5% 90%
      );

    transform:
      rotate(
        -1.1deg
      )
      skewX(
        -1.6deg
      )
      scaleX(
        0
      );
  }

  .marker-group::after {
    bottom: 0.035em;

    height: 0.52em;

    background:
      rgba(
        239,
        255,
        103,
        0.28
      );

    clip-path:
      polygon(
        0 12%,
        14% 3%,
        29% 10%,
        45% 2%,
        63% 8%,
        79% 1%,
        100% 11%,
        99% 90%,
        82% 96%,
        66% 88%,
        48% 98%,
        31% 91%,
        13% 100%,
        1% 88%
      );

    transform:
      rotate(
        0.45deg
      )
      scaleX(
        0
      );
  }

  .marker-highlight.is-highlighted
  .marker-group::before {
    animation:
      role-highlight-swipe
      780ms
      var(
        --marker-group-delay,
        180ms
      )
      cubic-bezier(
        0.22,
        1,
        0.36,
        1
      )
      forwards;
  }

  .marker-highlight.is-highlighted
  .marker-group::after {
    animation:
      role-highlight-second-pass
      900ms
      calc(
        var(
          --marker-group-delay,
          180ms
        ) +
        110ms
      )
      cubic-bezier(
        0.22,
        1,
        0.36,
        1
      )
      forwards;
  }

  .marker-word {
    display: inline-block;
  }

  .role-typed-word {
    display: inline-block;

    visibility: hidden;

    opacity: 0;

    transform:
      translateY(
        0.22em
      );

    transition:
      opacity
        220ms
        ease,
      transform
        300ms
        cubic-bezier(
          0.22,
          1,
          0.36,
          1
        );
  }

  .role-typed-word.is-visible {
    visibility: visible;
    opacity: 1;

    transform:
      translateY(0);
  }

  /*
   * RESPONSIBILITY LIST
   */

  .role-responsibility-list {
    display: grid;

    gap: 12px;

    width: 100%;
    max-width: 790px;
  }

  .role-responsibility-line {
    display: block;

    color: #111111;

    font-size:
      clamp(
        27px,
        3.25vw,
        44px
      );

    font-weight: 450;

    line-height: 1.07;

    letter-spacing:
      -0.038em;

    text-align: left;

    opacity: 0;

    transform:
      translateY(
        18px
      );

    transform-origin:
      left center;

    transition:
      opacity
        500ms
        ease,
      color
        450ms
        ease,
      transform
        650ms
        cubic-bezier(
          0.22,
          1,
          0.36,
          1
        );
  }

  .role-responsibility-line.is-visible {
    opacity: 0.35;

    transform:
      translateY(0);
  }

  .role-responsibility-line.is-current {
    opacity: 1;

    transform:
      translateY(0)
      scale(1.025);
  }

  .role-responsibility-line
  .role-action-handwritten {
    display: inline-block;

    margin-right: 0.04em;

    font-family:
      'Ugly Handwriting',
      cursive;

    font-size: 1.1em;

    font-weight: 400;

    line-height: 0.92;

    letter-spacing: 0;

    word-spacing:
      -0.16em;

    transform:
      translateY(
        0.025em
      )
      rotate(
        -1deg
      );

    transform-origin:
      center;
  }

  .role-responsibility-line span {
    font-weight: 520;
  }

  /*
   * FINAL EQUATION
   */

  .qualified-equation {
    display: grid;

    grid-template-columns:
      minmax(
        210px,
        255px
      )
      48px
      minmax(
        190px,
        230px
      )
      48px
      minmax(
        215px,
        255px
      );

    align-items: center;

    justify-content: center;

    column-gap:
      clamp(
        16px,
        1.8vw,
        26px
      );

    width:
      min(
        1040px,
        100%
      );
  }

  /*
   * STEPS
   */

  .qualified-step,
  .qualified-operator {
    opacity: 0;

    transform:
      translateY(
        14px
      );

    transition:
      opacity
        450ms
        ease,
      transform
        650ms
        cubic-bezier(
          0.22,
          1,
          0.36,
          1
        );
  }

  .qualified-step.is-visible,
  .qualified-operator.is-visible {
    opacity: 1;

    transform:
      translateY(0);
  }

  /*
   * FOCUS SEQUENCE
   *
   * STEP 01:
   * first focus.
   *
   * STEP 02:
   * Step 01 recedes.
   *
   * STEP 03:
   * Step 01 + Step 02 recede,
   * Step 03 gets focus.
   *
   * Then the whole equation
   * returns to full focus.
   */

  .qualified-step.is-muted,
  .qualified-operator.is-muted {
    opacity: 0.3;

    transform:
      translateY(0)
      scale(0.96);
  }

  .qualified-equation.is-finalizing
  .qualified-step.is-muted,
  .qualified-equation.is-finalizing
  .qualified-operator.is-muted {
    animation:
      qualified-return-focus
      520ms
      1750ms
      cubic-bezier(
        0.22,
        1,
        0.36,
        1
      )
      forwards;
  }

  .qualified-step {
    min-width: 0;

    text-align: left;
  }

  .qualified-step-eyebrow {
    display: block;

    margin-bottom: 12px;

    color: #9a9a9a;

    font-size: 10px;

    font-weight: 750;

    line-height: 1;

    letter-spacing:
      0.11em;

    text-transform:
      uppercase;
  }

  .qualified-step-copy {
    display: block;

    color: #111111;

    font-size:
      clamp(
        30px,
        3vw,
        42px
      );

    line-height: 0.95;

    letter-spacing:
      -0.045em;
  }

  .qualified-step-copy strong {
    display: block;

    font-weight: 820;
  }

  .qualified-step-copy span {
    display: block;

    margin-top: 7px;

    color: #555555;

    font-size: 0.72em;

    font-weight: 500;

    line-height: 1.03;

    letter-spacing:
      -0.035em;
  }

  .qualified-step-copy em,
  .qualified-step-emphasis {
    color: #111111;

    font-style: italic;

    font-weight: 720;
  }

  /*
   * STEP ONE
   */

  .qualified-step-account {
    width:
      min(
        240px,
        100%
      );
  }

  /*
   * STEP TWO
   */

  .qualified-step-attendance
  .qualified-step-copy {
    font-size:
      clamp(
        29px,
        2.8vw,
        39px
      );
  }

  /*
   * Same italic treatment used by
   * both Step 01 and Step 02.
   */

  .qualified-step-emphasis {
    display: block;

    margin-top: 5px;

    font-size: 0.92em;

    line-height: 0.98;
  }

  /*
   * + AND =
   */

  .qualified-operator {
    align-self: center;

    display: grid;

    place-items: center;

    width: 48px;

    margin-top: 22px;

    color: #8e8e8e;

    font-size:
      clamp(
        43px,
        4.4vw,
        58px
      );

    font-weight: 350;

    line-height: 0.8;

    letter-spacing:
      -0.05em;

    text-align: center;
  }

  .qualified-plus {
    transform-origin: center;
  }

  .qualified-equals {
    font-size:
      clamp(
        40px,
        4.2vw,
        55px
      );

    transform-origin: center;
  }

  /*
   * RESULT
   */

  .qualified-step-result {
    text-align: center;
  }

  .qualified-step-result
  .qualified-step-eyebrow {
    text-align: left;

    padding-left: 7px;
  }

  .qualified-result-lockup {
    position: relative;

    display: grid;

    place-items: center;

    width:
      clamp(
        220px,
        23vw,
        255px
      );

    min-height:
      clamp(
        105px,
        11vw,
        125px
      );
  }

  .qualified-result-label {
    position: relative;

    z-index: 3;

    display: block;

    color: #111111;

    font-size:
      clamp(
        28px,
        2.8vw,
        37px
      );

    font-weight: 830;

    line-height: 0.91;

    letter-spacing:
      -0.047em;

    text-align: center;
  }

  /*
   * HAND DRAWN CIRCLE
   */

  .qualified-result-circle {
    position: absolute;

    z-index: 2;

    top: 50%;
    left: 50%;

    width:
      clamp(
        220px,
        23vw,
        255px
      );

    height:
      clamp(
        104px,
        11vw,
        124px
      );

    overflow: visible;

    pointer-events: none;

    transform:
      translate(
        -50%,
        -50%
      )
      rotate(
        -1deg
      );
  }

  .qualified-circle-stroke,
  .qualified-arrow-stroke {
    fill: none;

    stroke: #111111;

    stroke-linecap: round;

    stroke-linejoin: round;
  }

  .qualified-circle-stroke {
    stroke-width: 3.4;

    vector-effect:
      non-scaling-stroke;

    stroke-dasharray: 1;

    stroke-dashoffset: 1;

    filter:
      url(
        #qualified-circle-pencil
      );
  }

  .qualified-step-result.is-visible
  .qualified-circle-main {
    animation:
      qualified-circle-draw
      900ms
      300ms
      cubic-bezier(
        0.22,
        1,
        0.36,
        1
      )
      forwards;
  }

  .qualified-circle-echo {
    stroke-width: 1.5;

    opacity: 0.4;
  }

  .qualified-step-result.is-visible
  .qualified-circle-echo {
    animation:
      qualified-circle-draw
      540ms
      900ms
      cubic-bezier(
        0.22,
        1,
        0.36,
        1
      )
      forwards;
  }

  /*
   * MONEY
   */

  .qualified-reward-callout {
    position: absolute;

    z-index: 5;

   right: -22px;
  bottom: -68px;

    display: flex;

    align-items:
      flex-start;

    white-space:
      nowrap;
  }

  .qualified-reward-arrow {
    width: 58px;

    height: 48px;

    margin-top: -21px;

    margin-right: -7px;

    overflow: visible;

    transform:
      rotate(
        -3deg
      );
  }

  .qualified-arrow-stroke {
    stroke-width: 2.5;

    vector-effect:
      non-scaling-stroke;

    stroke-dasharray: 1;

    stroke-dashoffset: 1;

    filter:
      url(
        #qualified-arrow-pencil
      );
  }

  .qualified-step-result.is-visible
  .qualified-arrow-curve {
    animation:
      qualified-arrow-draw
      520ms
      1240ms
      ease-out
      forwards;
  }

  .qualified-step-result.is-visible
  .qualified-arrow-head {
    animation:
      qualified-arrow-draw
      280ms
      1530ms
      ease-out
      forwards;
  }

  /*
   * Lower + larger $$$$
   */

  .qualified-reward-callout strong {
    display: block;

    margin-top: 16px;
    margin-left: 3px;

    color: #111111;

    font-family:
      'Ugly Handwriting',
      cursive;

    font-size:
      clamp(
        36px,
        3.9vw,
        50px
      );

    font-weight: 500;

    line-height: 0.8;

    letter-spacing: 0;

    opacity: 0;

    transform:
      translateY(
        7px
      )
      rotate(
        3deg
      )
      scale(
        0.84
      );
  }

  .qualified-step-result.is-visible
  .qualified-reward-callout strong {
    animation:
      qualified-money-pop
      430ms
      1720ms
      cubic-bezier(
        0.2,
        1.35,
        0.35,
        1
      )
      forwards;
  }

  /*
   * ANIMATIONS
   */

  @keyframes
  role-scene-enter {
    from {
      opacity: 0;

      transform:
        translateY(
          15px
        )
        scale(
          0.988
        );
    }

    to {
      opacity: 1;

      transform:
        translateY(0)
        scale(1);
    }
  }

  @keyframes
  role-highlight-swipe {
    from {
      transform:
        rotate(
          -1.1deg
        )
        skewX(
          -1.6deg
        )
        scaleX(0);
    }

    to {
      transform:
        rotate(
          -1.1deg
        )
        skewX(
          -1.6deg
        )
        scaleX(1);
    }
  }

  @keyframes
  role-highlight-second-pass {
    from {
      transform:
        rotate(
          0.45deg
        )
        scaleX(0);
    }

    to {
      transform:
        rotate(
          0.45deg
        )
        scaleX(1);
    }
  }

  @keyframes
  qualified-return-focus {
    to {
      opacity: 1;

      transform:
        translateY(0)
        scale(1);
    }
  }

  @keyframes
  qualified-circle-draw {
    to {
      stroke-dashoffset: 0;
    }
  }

  @keyframes
  qualified-arrow-draw {
    to {
      stroke-dashoffset: 0;
    }
  }

  @keyframes
  qualified-money-pop {
    to {
      opacity: 1;

      transform:
        translateY(0)
        rotate(0)
        scale(1);
    }
  }

  /*
   * MOBILE
   */

  @media (
    max-width:
    760px
  ) {
    .role-kinetic-slide {
      min-height: 520px;

      padding: 24px;
    }

    .role-single-message h1 {
      font-size:
        clamp(
          38px,
          10.5vw,
          50px
        );
    }

    .role-responsibility-list {
      gap: 16px;
    }

    .role-responsibility-line {
      font-size:
        clamp(
          26px,
          7.2vw,
          38px
        );
    }

    .role-responsibility-line
    .role-action-handwritten {
      margin-right:
        0.03em;

      font-size:
        1.08em;

      word-spacing:
        -0.15em;
    }

    /*
     * EQUATION MOBILE
     */

    .qualified-equation {
      grid-template-columns:
        minmax(
          0,
          1.28fr
        )
        auto
        minmax(
          0,
          1fr
        )
        auto
        minmax(
          0,
          1.08fr
        );

      column-gap: 5px;

      width: 100%;
    }

    .qualified-step-eyebrow {
      margin-bottom: 6px;

      font-size: 7px;
    }

    .qualified-step-copy {
      font-size:
        clamp(
          13px,
          3.9vw,
          19px
        );

      line-height: 0.97;
    }

    .qualified-step-copy span {
      margin-top: 3px;

      font-size: 0.72em;
    }

    .qualified-step-attendance
    .qualified-step-copy {
      font-size:
        clamp(
          12px,
          3.6vw,
          18px
        );
    }

    .qualified-step-emphasis {
      margin-top: 2px;
    }

    .qualified-operator {
      width: 0.7em;

      margin-top: 11px;

      font-size:
        clamp(
          22px,
          6vw,
          30px
        );
    }

    .qualified-equals {
      font-size:
        clamp(
          21px,
          5.8vw,
          28px
        );
    }

    .qualified-step-result
    .qualified-step-eyebrow {
      padding-left: 0;
    }

    .qualified-result-lockup {
      width:
        clamp(
          118px,
          32vw,
          155px
        );

      min-height:
        clamp(
          56px,
          15vw,
          72px
        );
    }

    .qualified-result-label {
      font-size:
        clamp(
          13px,
          3.8vw,
          18px
        );
    }

    .qualified-result-circle {
      width:
        clamp(
          118px,
          32vw,
          155px
        );

      height:
        clamp(
          56px,
          15vw,
          72px
        );
    }

    .qualified-reward-callout {
      right: -3px;

      bottom: -27px;
    }

    .qualified-reward-arrow {
      width: 38px;

      height: 31px;

      margin-top: -13px;

      margin-right: -5px;
    }

    .qualified-arrow-stroke {
      stroke-width: 2.1;
    }

    .qualified-reward-callout strong {
      margin-top: 9px;
      margin-left: 2px;

      font-size:
        clamp(
          22px,
          6vw,
          30px
        );
    }
  }

  /*
   * REDUCED MOTION
   */

  @media (
    prefers-reduced-motion:
    reduce
  ) {
    .role-single-message,
    .role-responsibility-scene,
    .qualified-equation-scene,
    .role-responsibility-line,
    .qualified-step,
    .qualified-operator {
      animation-duration:
        1ms !important;

      animation-delay:
        0ms !important;

      transition-duration:
        1ms !important;
    }

    .marker-highlight.is-highlighted
    .marker-group::before,
    .marker-highlight.is-highlighted
    .marker-group::after,
    .qualified-circle-stroke,
    .qualified-arrow-stroke,
    .qualified-reward-callout strong {
      animation-duration:
        1ms !important;

      animation-delay:
        0ms !important;
    }
  }
`;

YourRole.title =
  'Your role';

YourRole.steps =
  STEPS;